import {
  addBackgroundThemeImage,
  renderAdditionalBackgroundThemeImages,
} from "@frontend/group/modules/additional-fields";
import {
  getProductImageFields, uploadProductImage,
  uploadProductImageAndDraw,
  handleProductImageUpload
} from "@frontend/group/modules/add-product-image/add-product-image-service";
import {editorControlEventBroker, EVENTS} from "@frontend/group/modules/editor-control-popup/event-broker";
import {FIELD_TYPE_PRODUCT_IMAGE, FIELD_TYPE_BACKGROND_THEME_IMAGE} from "@frontend/constants/type-fields-of-template";
import {ADDITIONAL_BACKGROUND_THEME_IMAGE_PREFIX_ID} from "@frontend/constants/templates-field-prefixes";
import {importHyperlinksEventBroker} from '@frontend/group/modules/import-hyperlinks/event-broker';
import store from "@frontend/store";
import {MUTATIONS} from "@frontend/store/file-browser-product-selector";
import {ShowPageScopeCollector} from "@frontend/group/modules/scope-collectors/show-page-collector";
import {parseBriefReferenceDragList} from "@frontend/components/briefs/briefReferenceUtils";
import {parseSidebarFileDragList} from "@frontend/components/modules/layouts-page/sidebar-file-drag";

const getScopePage = () => ShowPageScopeCollector.get([
  'handleObjectMoving',
  'replaceBackgroundImage',
  'reorderObjectCanvas',
  'canvas_data',
  'replaceImageForAdditionalBackgroundThemeImage',
  'updateLayoutChanges',
  'setAdditionalBackgroundImage'
]);

const DRAG_OVER_BORDER = '2px dashed #1c7964';
const DEFAULT_BORDER = 'none';
const CANVAS_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

function extensionOf(value) {
  const match = String(value || '').split('?')[0].match(/\.([a-z0-9]+)$/i);
  return match ? match[1].toLowerCase() : '';
}

function imageMime(blobType, name, url) {
  const type = String(blobType || '').toLowerCase();
  if (type === 'image/jpg') {
    return 'image/jpeg';
  }
  if (CANVAS_IMAGE_TYPES.includes(type)) {
    return type;
  }
  const ext = extensionOf(name) || extensionOf(url);
  if (ext === 'png') return 'image/png';
  if (ext === 'webp') return 'image/webp';
  if (ext === 'jpg' || ext === 'jpeg') return 'image/jpeg';
  return type;
}

function imageFilename(name, url, type) {
  const fromUrl = decodeURIComponent(String(url || '').split('?')[0].split('/').pop() || '');
  const base = String(name || '').trim() || fromUrl || 'image';
  if (/\.(jpe?g|png|webp)$/i.test(base)) {
    return base;
  }
  const ext = type === 'image/png' ? 'png' : type === 'image/webp' ? 'webp' : 'jpg';
  return `${base}.${ext}`;
}

async function fileFromBriefReference(ref) {
  const response = await fetch(ref.url, { credentials: 'same-origin' });
  if (!response.ok) {
    throw new Error(`Failed to fetch brief image (${response.status})`);
  }
  const blob = await response.blob();
  const type = imageMime(blob.type, ref.name, ref.url);
  return new File([blob], imageFilename(ref.name, ref.url, type), { type });
}

// The type dialog closes when the current file name matches the last file.
// Two brief images can share a name, so keep those names distinct.
function withUniqueFileName(file, usedNames) {
  if (!usedNames.has(file.name)) {
    usedNames.add(file.name);
    return file;
  }
  const dot = file.name.lastIndexOf('.');
  const stem = dot > 0 ? file.name.slice(0, dot) : file.name;
  const ext = dot > 0 ? file.name.slice(dot) : '';
  let index = 2;
  let name = `${stem}-${index}${ext}`;
  while (usedNames.has(name)) {
    index += 1;
    name = `${stem}-${index}${ext}`;
  }
  usedNames.add(name);
  return new File([file], name, { type: file.type });
}

class TemplateDragDropService {
  constructor(previews, template_data) {
    this.previews = previews;
    this.template_data = template_data;
    this.currentFile = null;
    this.currentTemplate = null;
    this.isReplacedOnce = false;
    this.isConfirmedOnce = false;
    this.confirmedImageType = null;
    this.files = null;
    this.batchHandled = false;
  }

  initializeTemplates(templates) {
    templates.forEach(template => {
      this.attachDragHandlers(template);
    });
  }

  attachDragHandlers(template) {
    template.addEventListener('dragover', (e) => this.handleDragOver(e, template));
    template.addEventListener('dragleave', (e) => this.handleDragLeave(e, template));
    template.addEventListener('drop', (e) => this.handleDrop(e, template));
  }

  handleDragOver(e, template) {
    e.preventDefault();
    e.stopPropagation();
    template.style.border = DRAG_OVER_BORDER;
  }

  handleDragLeave(e, template) {
    e.preventDefault();
    e.stopPropagation();
    template.style.border = DEFAULT_BORDER;
  }

  async handleDrop(e, template) {
    e.preventDefault();
    e.stopPropagation();
    template.style.border = DEFAULT_BORDER;
    this.isReplacedOnce = false;
    this.isConfirmedOnce = false;
    this.confirmedImageType = null;
    this.batchHandled = false;

    const instanceId = $(template).closest('[data-instance]').data('instance')
    if (!instanceId) {
      toastr.error('Could not determine template instance');
      return;
    }

    const briefRefs = parseBriefReferenceDragList(e).filter((ref) => !ref.kind || ref.kind === 'image');
    if (briefRefs.length) {
      await this.handleBriefReferenceDrop(briefRefs, template, instanceId, e);
      return;
    }

    const sidebarFiles = parseSidebarFileDragList(e);
    if (sidebarFiles.length) {
      await this.handleBriefReferenceDrop(sidebarFiles, template, instanceId, e, true);
      return;
    }

    const files = e.dataTransfer.files;
    if (!files.length) return;

    // Check for JSON files first
    const jsonFiles = this.filterJsonFiles(files);
    if (jsonFiles.length > 0) {
      // Handle JSON files - either single or multiple
      if (jsonFiles.length === 1) {
        this.handleJsonDrop(jsonFiles[0], instanceId);
      } else {

        this.handleMultipleJsonDrop(jsonFiles, instanceId);
      }
      return;
    }

    // Handle image files
    const imageFiles = Array.from(files).filter((file) => this.isValidImageFile(file));
    if (imageFiles.length !== files.length) {
      toastr.error('Invalid file type. Please use jpg, png, webp, or json files.');
    }
    if (!imageFiles.length) return;

    this.files = imageFiles;
    for (const firstFile of imageFiles) {
      await this.handleImageFile(firstFile, template, instanceId, e, false, null, false, imageFiles.length);
      if (this.batchHandled) {
        break;
      }
    }

    $('.loadingOverlay').fadeOut();
  }

  handleMultipleJsonDrop(files, instanceId) {
    // Store the files in global variables
    window.droppedJsonFiles = Array.from(files);
    window.droppedInstanceId = instanceId;
    window.bulkJsonUpload = true;

    toastr.info(`Processing ${files.length} JSON files. Files will be applied to templates in order.`);

    // Fire the init event to open the modal
    importHyperlinksEventBroker.fire(EVENTS.INIT);
  }

  handleJsonDrop(file, instanceId) {
    // Store the file in global variables
    window.droppedJsonFiles = [file];
    window.droppedInstanceId = instanceId;
    window.bulkJsonUpload = false;

    // Fire the init event to open the modal
    importHyperlinksEventBroker.fire(EVENTS.INIT);
  }

  filterJsonFiles(files) {
    return Array.from(files).filter(file =>
      file.type === 'application/json' || file.name.toLowerCase().endsWith('.json')
    );
  }

  isValidImageFile(file) {
    return CANVAS_IMAGE_TYPES.includes(file?.type);
  }

  async handleBriefReferenceDrop(refs, template, instanceId, dropEvent, useSidebarPreset = false) {
    if (useSidebarPreset) {
      this.sidebarBackgroundIndex = 0;
    }
    $('.loadingOverlay').fadeIn();
    $('.loadingOverlay-text').html(refs.length > 1 ? 'Adding images...' : 'Adding image...');

    if (useSidebarPreset && this.getSidebarImageType() === 'background' && refs.length > 1) {
      this.batchHandled = true;
      this.sidebarBackgroundIndex = 0;
      await this.handleBackgroundImagesDrop([], instanceId, false, true, refs);
      $('.loadingOverlay').fadeOut();
      return;
    }

    this.files = [];
    const usedNames = new Set();
    try {
      const fetchedFiles = await Promise.all(refs.map(async ref => {
        try {
          const file = await fileFromBriefReference(ref);

          if (!this.isValidImageFile(file)) {
            toastr.error('Invalid file type. Please use jpg, png, webp, or json files.');
            return null;
          }

          return file;
        } catch (error) {
          console.error('Failed to load brief image:', error);
          toastr.error('Could not add a brief image to the canvas.');
          return null;
        }
      }));

      // Promise.all preserves the original ref order; make duplicate names
      // unique after all downloads have completed.
      this.files = fetchedFiles
        .filter(Boolean)
        .map(file => withUniqueFileName(file, usedNames));
    } finally {
      // The type dialog sits under this overlay. Hide it before asking how to place the images.
      $('.loadingOverlay').fadeOut();
    }

    const files = this.files || [];
    if (!files.length) {
      return;
    }

    this.files = files;
    this.batchHandled = false;
    if (useSidebarPreset && files.length > 1) {
      const imageType = this.getSidebarImageType();
      const targetPreview = this.previews.find(preview => preview.id === instanceId);
      this.batchHandled = true;
      if (imageType === 'product') {
        await this.handleProductImagesDrop(files, targetPreview);
      } else {
        await this.handleBackgroundImagesDrop(files, instanceId, false, true);
      }
      $('.loadingOverlay').fadeOut();
      return;
    }

    for (const file of files) {
      const confirmedBefore = this.isConfirmedOnce;
      const replacedBefore = this.isReplacedOnce;
      await this.handleImageFile(
        file,
        template,
        instanceId,
        dropEvent,
        true,
        useSidebarPreset ? this.getSidebarImageType() : null,
        useSidebarPreset,
        files.length
      );
      if (this.batchHandled) {
        break;
      }
      // Closing the type dialog cancels the rest of this drop.
      if (!confirmedBefore && !replacedBefore && !this.isConfirmedOnce && !this.isReplacedOnce) {
        break;
      }
    }
    $('.loadingOverlay').fadeOut();
  }

  isPlaceholder(object, instanceId, type = null) {
    const { canvas_data } = getScopePage();
    const templateField = canvas_data[instanceId]?.templateInstance?.fields?.find(field => field.id === object?.fieldId);
    const fileName = templateField?.options?.filename;

    // check if image is a placeholder
    // placeholders are uploaded in /uploads directory
    if (type === 'background' && object?.src?.includes('uploads')) {
      return true;
    }

    // check if product image is a placeholder
    if (type === 'productImage' && fileName) {
      const templateFieldName = fileName?.split('/').pop();
      const canvasObjectName = object.fileName?.split('/').pop();

      //if different = product image has been replaced at least once
      return templateFieldName === canvasObjectName;
    }

    return false;
  }

  async handleImageFile(
    file,
    template,
    instanceId,
    dropEvent,
    dontReplaceProductImage = false,
    presetImageType = null,
    skipDropTarget = false,
    totalObjectsCount = null
  ) {
    this.currentFile = file;
    this.currentTemplate = template;

    const targetPreview = this.previews.find(preview => preview.id === instanceId);
    // Check if dropped on product image
    const replaceProductImage = dontReplaceProductImage ? null : this.getObjectOnWhichDrop({
      dropEvent,
      targetPreview,
      template
    });
    if (replaceProductImage && !this.isReplacedOnce) {
      $('.loadingOverlay').fadeIn();
      $('.loadingOverlay-text').html('Replacing Product Images...');
      this.isReplacedOnce = true;
      // check if product image is a static placeholder
      if (this.isPlaceholder(replaceProductImage, instanceId, 'productImage') && window.isBannerBuilder) {
        for (const preview of this.previews) {
          const foundObject = preview.canvas.getObjects().find((obj) =>
            obj.fieldType === FIELD_TYPE_PRODUCT_IMAGE &&
            obj.element_id === replaceProductImage.element_id
          );

          if (foundObject) {
            await this.handleProductImageDrop({file, targetPreview: preview, replaceProductImage: foundObject});
          }
        }
        return;
      }

      await this.handleProductImageDrop({file, targetPreview, replaceProductImage});

      return;
    }

    const replaceBackgroundImage = skipDropTarget ? null : this.getObjectOnWhichDrop({
      dropEvent,
      targetPreview,
      template,
      type: 'background'
    });
    if (replaceBackgroundImage && !this.isReplacedOnce) {
      $('.loadingOverlay').fadeIn();
      $('.loadingOverlay-text').html('Replacing Images...');
      this.isConfirmedOnce = true;
      if (this.isPlaceholder(replaceBackgroundImage, instanceId, 'background') && window.isBannerBuilder) {
        for (const preview of this.previews) {
          const foundObject = preview.canvas.getObjects().find((obj) =>
            obj.fieldType === FIELD_TYPE_BACKGROND_THEME_IMAGE &&
            obj.element_id === replaceBackgroundImage.element_id
          );

          if (foundObject) {
            await this.handleBackgroundImageDrop(this.currentFile, preview.id, null, replaceBackgroundImage);
          }
        }

        this.isReplacedOnce = true;
        return;
      }

      // if image is already replace, skip to creating a new background image
      if (!this.isReplacedOnce) {
        await this.handleBackgroundImageDrop(this.currentFile, instanceId, null, replaceBackgroundImage);
        this.isReplacedOnce = true;
      }

      return;
    }

    // The sidebar preset is applied once; the rest of a multi-image drop follows that choice.
    if (this.isConfirmedOnce) {
      if (this.confirmedImageType === 'product') {
        await this.handleProductImageDrop({ file: this.currentFile, targetPreview });
      } else {
        await this.handleSidebarBackgroundImageDrop(this.currentFile, instanceId, targetPreview, totalObjectsCount, skipDropTarget);
      }
      return;
    }

    if (presetImageType) {
      this.isConfirmedOnce = true;
      this.confirmedImageType = presetImageType;
      if (this.confirmedImageType === 'product') {
        await this.handleProductImageDrop({ file: this.currentFile, targetPreview });
      } else {
        await this.handleSidebarBackgroundImageDrop(this.currentFile, instanceId, targetPreview, totalObjectsCount, true);
      }
      return;
    }

    await this.showImageTypeModal(instanceId, targetPreview);
  }

  getSidebarImageType() {
    return window.sidebarImageType === 'product' ? 'product' : 'background';
  }

  async showImageTypeModal(instanceId, targetPreview) {
    return new Promise((resolve) => {
      const modal = $('#image-type-selection-modal');

      if (window.isBannerBuilder) {
        document.querySelector('#showApplyToAllTemplates').classList.remove('d-none');
      }
      modal.modal('show');

      $('#confirm-image-type').off('click');
      $('#confirm-image-type').one('click', async () => {
        await this.handleImageTypeConfirmation(instanceId, targetPreview, modal);
        resolve();
      });

      modal.one('hidden.bs.modal', () => {
        $('#confirm-image-type').off('click');
        this.currentFile = null;
        this.currentTemplate = null;
        resolve();
      });
    });
  }

  async handleImageTypeConfirmation(instanceId, targetPreview, modal) {
    const confirmButton = $('#confirm-image-type');
    const selectedType = document.querySelector('input[name="imageType"]:checked').value;
    this.setButtonLoading(confirmButton, true);

    if (window.isBannerBuilder && document.querySelector('#applyToAllTemplates').checked) {
      try {
        this.isConfirmedOnce = true;
        this.confirmedImageType = selectedType;
        if (selectedType === 'background') {
          for (const preview of this.previews) {
            await this.handleBackgroundImageDrop(this.currentFile, preview.id, preview?.template);
          }
        } else {
          const result = await handleProductImageUpload(this.currentFile, instanceId);
          for (const preview of this.previews) {
            await this.handleProductImageDrop({file: this.currentFile, targetPreview: preview, recentImage: result});
          }
        }
      } finally {
        this.setButtonLoading(confirmButton, false);
        modal.modal('hide');
      }
      return;
    }

    try {
      this.isConfirmedOnce = true;
      this.confirmedImageType = selectedType;
      if (selectedType === 'background') {
        if (this.files?.length > 1) {
          this.batchHandled = true;
          await this.handleBackgroundImagesDrop(this.files, instanceId);
        } else {
          await this.handleBackgroundImageDrop(this.currentFile, instanceId);
        }
      } else if (this.files?.length > 1) {
        this.batchHandled = true;
        targetPreview.batchProductImageMode = true;
        await this.handleProductImageDrop({file: this.files, targetPreview});
      } else {
        await this.handleProductImageDrop({file: this.currentFile, targetPreview});
      }
    } finally {
      this.setButtonLoading(confirmButton, false);
      modal.modal('hide');
    }
  }

  async handleBackgroundImagesDrop(
    files,
    instanceId,
    closeModal = true,
    useSidebarPlacement = false,
    sourceRefs = []
  ) {
    const { id: template_id, customer_id } = this.template_data;
    const formData = new FormData();
    const imageFiles = Array.from(files);
    const uploadCount = sourceRefs.length || imageFiles.length;

    formData.append('customer_id', customer_id);
    formData.append('template_id', template_id);
    formData.append('theme_id', $('#theme').val());
    imageFiles.forEach((file, index) => {
      const position = index + 1;
      formData.append(`file_${position}`, file);
      formData.append(`filename_${position}`, file.name);
    });
    sourceRefs.forEach((ref, index) => {
      const position = index + 1;
      formData.append(`url_${position}`, ref.url);
      formData.append(`filename_${position}`, ref.name || `image-${position}.png`);
    });

    try {
      const uploadEndpoint = sourceRefs.length
        ? '/banner/upload_bk_image_by_url'
        : '/banner/upload_bk_image';
      const response = await axios.post(uploadEndpoint, formData);
      const uploadedImages = Array.isArray(response.data) ? response.data : [];
      const currentPreview = this.previews.find(preview => preview.id === instanceId);
      if (!currentPreview || uploadedImages.length !== uploadCount) {
        toastr.error('The bulk background-image upload returned an incomplete response.');
        return;
      }

      const canvas = currentPreview.canvas;
      const previousRenderOnAddRemove = canvas.renderOnAddRemove;
      const previousSkipRender = canvas.skipRender;
      canvas.renderOnAddRemove = false;
      canvas.skipRender = true;
      try {
        for (const uploadedImage of uploadedImages) {
          await addBackgroundThemeImage({
            currentInstanceId: instanceId,
            currentPreview,
            additional_background_images: `/share?file=${uploadedImage.path}`,
          }, {
            deferCanvasUpdate: true,
          });
        }

        await renderAdditionalBackgroundThemeImages({
          currentInstanceId: instanceId,
          currentPreview,
        }, {deferRender: true});
        if (useSidebarPlacement) {
          for (const [placementIndex] of uploadedImages.entries()) {
            const {canvas_data} = getScopePage();
            const objectIndex = canvas_data[instanceId].additional_background_theme_image.length
              - uploadedImages.length + placementIndex;
            this.applySidebarBackgroundPlacement(currentPreview, placementIndex, objectIndex, uploadedImages.length);
          }
        }
      } finally {
        canvas.renderOnAddRemove = previousRenderOnAddRemove;
        canvas.skipRender = previousSkipRender;
      }

      canvas.renderAll();
      if (!useSidebarPlacement) {
        toastr.success(`${uploadedImages.length} images saved successfully`);
      }
    } catch (error) {
      toastr.error('Failed to upload background images');
      console.error(error);
    } finally {
      if (closeModal) {
        this.setButtonLoading($('#confirm-image-type'), false);
        $('#image-type-selection-modal').modal('hide');
      }
    }
  }

  applySidebarBackgroundPlacement(preview, placementIndex, objectIndex, totalObjectsCount = null) {
    const canvas = preview?.canvas;
    if (!canvas) {
      return;
    }

    const {canvas_data} = getScopePage();
    const templateData = canvas_data?.[preview.id];
    const templateWidth = Number(templateData?.dimension?.width)
      || Number(canvas.getWidth?.() || canvas.width);
    const templateHeight = Number(templateData?.dimension?.height)
      || Number(canvas.getHeight?.() || canvas.height);
    const allObjects = canvas.getObjects();
    const backgroundObjects = allObjects
      .filter(object => object.id?.startsWith(ADDITIONAL_BACKGROUND_THEME_IMAGE_PREFIX_ID));
    const objectId = `${ADDITIONAL_BACKGROUND_THEME_IMAGE_PREFIX_ID}${objectIndex}`;
    const object = backgroundObjects.find(candidate => candidate.id === objectId);
    if (!object?.width || !object?.height) {
      return;
    }

    const minDimension = Math.min(templateWidth, templateHeight);
    const boxSize = minDimension * 0.2;
    const imageScale = Math.min(boxSize / object.width, boxSize / object.height);
    let offset = (boxSize / 4) * placementIndex;
    if (totalObjectsCount && ((totalObjectsCount * offset + boxSize) > minDimension / 2)) {
      offset = ((minDimension - boxSize) / 2) / totalObjectsCount * placementIndex;
    }
    const center = {
      left: templateWidth / 2,
      top: templateHeight / 2,
    };
    const left = center.left + offset;
    const top = center.top + offset;

    const layoutData = templateData?._data || preview.data || {};
    const scaleX = [...(layoutData.add_bk_img_scale || [])];
    const scaleY = [...(layoutData.add_bk_img_scale_y || [])];
    const currentScaleX = Number(object.scaleX) || 1;
    const currentScaleY = Number(object.scaleY) || 1;
    const previousScaleX = Number(scaleX[objectIndex]) || 1;
    const previousScaleY = Number(scaleY[objectIndex]) || 1;
    const persistedScaleX = previousScaleX * imageScale / currentScaleX;
    const persistedScaleY = previousScaleY * imageScale / currentScaleY;

    object.set({
      left,
      top,
      scaleX: imageScale,
      scaleY: imageScale,
    });
    object.setCoords();

    const {updateLayoutChanges} = getScopePage();
    const offsetX = [...(layoutData.add_bk_img_offset_x || [])];
    const offsetY = [...(layoutData.add_bk_img_offset_y || [])];

    scaleX[objectIndex] = persistedScaleX;
    scaleY[objectIndex] = persistedScaleY;
    offsetX[objectIndex] = left;
    offsetY[objectIndex] = top;
    updateLayoutChanges(preview.id, 'add_bk_img_scale', scaleX);
    updateLayoutChanges(preview.id, 'add_bk_img_scale_y', scaleY);
    updateLayoutChanges(preview.id, 'add_bk_img_offset_x', offsetX);
    updateLayoutChanges(preview.id, 'add_bk_img_offset_y', offsetY);
  }

  async handleSidebarBackgroundImageDrop(file, instanceId, targetPreview, totalObjectsCount, silent = false) {
    const {canvas_data} = getScopePage();
    const objectIndex = canvas_data[instanceId].additional_background_theme_image.length;
    await this.handleBackgroundImageDrop(file, instanceId, null, null, silent);
    this.applySidebarBackgroundPlacement(
      targetPreview,
      this.sidebarBackgroundIndex || 0,
      objectIndex,
      totalObjectsCount
    );
    targetPreview?.canvas?.renderAll();
    this.sidebarBackgroundIndex = (this.sidebarBackgroundIndex || 0) + 1;
  }

  async handleProductImagesDrop(files, targetPreview) {
    if (!targetPreview) {
      toastr.error('Could not determine template instance');
      return;
    }

    try {
      const existingImages = getProductImageFields(targetPreview);
      const recentImage = await handleProductImageUpload(files, targetPreview.id, existingImages);
      if (!recentImage) {
        return;
      }

      await uploadProductImageAndDraw({
        file: files,
        canvas: targetPreview.canvas,
        selectedLayoutId: targetPreview.id,
        existingImages,
        recentImage,
      });
    } catch (error) {
      toastr.error('Failed to upload product images');
      console.error(error);
    }
  }


  setButtonLoading(button, isLoading) {
    if (isLoading) {
      button.prop('disabled', true);
      button.html('<i class="fa fa-spinner fa-spin"></i> Processing...');
    } else {
      button.prop('disabled', false);
      button.html('Confirm');
    }
  }

  async handleBackgroundImageDrop(file, instanceId, customTemplateData = null, existingImage = null, silent = false) {
    let { id: template_id, customer_id } = this.template_data;

    if (customTemplateData) {
      ({ id: template_id, customer_id } = customTemplateData);
    } 

    const theme_id = $('#theme').val();
    const formData = new FormData();
    
    formData.append('customer_id', customer_id);
    formData.append('template_id', template_id);
    formData.append('theme_id', theme_id);
    formData.append('file_1', file);
    formData.append('filename_1', file.name);

    try {
      const data = await axios.post('/banner/upload_bk_image', formData);
      if (!silent) {
        toastr.success('Image saved successfully');
      }
      const name = data.data[0].path;
      const uploadedImageUrl = `/share?file=${name}`;
      const currentPreview = this.previews.find(preview => preview.id === instanceId);
      const options = {
        currentInstanceId: instanceId,
        currentPreview,
        additional_background_images: uploadedImageUrl
      };

      //if existingImage replace the image instead of adding a new one
      if (existingImage) {
        const { replaceBackgroundImage, 
          handleObjectMoving, 
          reorderObjectCanvas, 
          replaceImageForAdditionalBackgroundThemeImage, 
          updateLayoutChanges, 
          setAdditionalBackgroundImage } = getScopePage();

        const isAdditionalBackgroundThemeImage = existingImage.id
                      && existingImage.id.startsWith(ADDITIONAL_BACKGROUND_THEME_IMAGE_PREFIX_ID);
        if (isAdditionalBackgroundThemeImage) {
          await replaceImageForAdditionalBackgroundThemeImage(
                          existingImage,
                          { path: uploadedImageUrl },
                          currentPreview,
                          updateLayoutChanges,
                          instanceId,
                          setAdditionalBackgroundImage,
                          handleObjectMoving,
                          existingImage
                        );
          return;
        }

        await replaceBackgroundImage(uploadedImageUrl, existingImage.id, instanceId, false)
        .then((object) => {
            reorderObjectCanvas(currentPreview.canvas)
            handleObjectMoving({
              target: object,
            });
            toastr.success('Image replaced successfully')
          });
        return;
      }

      await addBackgroundThemeImage(options);
    } catch (error) {
      toastr.error('Failed to upload background image');
      console.error(error);
    }
  }

  async handleProductImageDrop({file, targetPreview, replaceProductImage = null, recentImage = null}) {
    const canvas = targetPreview.canvas;

    //create new product image 
    if (!replaceProductImage){
      const existingImages = getProductImageFields(targetPreview);

      await uploadProductImageAndDraw({
        file,
        canvas,
        selectedLayoutId: targetPreview.id,
        existingImages,
        recentImage: recentImage
      });
      return;
    }

    //if replaceTargetId replace existing product image
    const uploadResponse = await uploadProductImage(file);
    const filename = uploadResponse.filenames[0];

    //set active element on which drop image no need when replace from sidebar (exit templateFieldId property)
    if (!replaceProductImage.templateFieldId) {
      canvas.setActiveObject(replaceProductImage);
    }
    const oldFilename = replaceProductImage.fileName;

    await editorControlEventBroker.fire(EVENTS.IMAGE_CHANGED, {
      action: 'productImage', value: filename.split('.')[0]
    });

    //need to remove swaped image from picked items in store to correctly work [[syncPickedItemsWithSelectedFileNames]]
    if (oldFilename) {
      const pickedItems = store.getters['fileBrowserProductSelector/pickedItems'];
      const indexInPicked = pickedItems.findIndex(item => item.fileId === oldFilename);
      if (indexInPicked !== -1) {
        pickedItems.splice(indexInPicked, 1);
        store.commit(`fileBrowserProductSelector/${MUTATIONS.SET_PICKED_ITEMS}`, pickedItems);
      }
    }
    
    toastr.success('Image replaced successfully');
  }

  getRealCanvasCoordinates(dropEvent, targetPreview, template) {
    const canvas = targetPreview.canvas;

    // Get canvas element bounding rect
    const rect = template.getBoundingClientRect();

    // Calculate mouse position relative to canvas element
    const mouseX = dropEvent.clientX - rect.left;
    const mouseY = dropEvent.clientY - rect.top;

    // Get the actual canvas dimensions
    const canvasWidth = canvas.width;
    const canvasHeight = canvas.height;

    // Get the displayed dimensions
    const displayWidth = rect.width;
    const displayHeight = rect.height;

    // Calculate scale factors
    const scaleX = canvasWidth / displayWidth;
    const scaleY = canvasHeight / displayHeight;

    // Convert to real canvas coordinates
    const canvasX = mouseX * scaleX;
    const canvasY = mouseY * scaleY;

    const zoom = canvas.getZoom();

    // Final coordinates accounting for zoom and pan
    const finalX = canvasX / zoom;
    const finalY = canvasY / zoom;

    return {
      positionX: finalX,
      positionY: finalY,
    };
  }

  isPointInsideObject(point, obj) {
    const {positionX, positionY} = point;
    const coords = obj.aCoords;

    if (!coords || !coords.tl || !coords.br) {
      return false;
    }

    // Check if point is within the bounding box
    return positionX >= coords.tl.x &&
      positionX <= coords.br.x &&
      positionY >= coords.tl.y &&
      positionY <= coords.br.y;
  }

  async addImageFiles(refs, instanceId) {
    const safeId = window.CSS && CSS.escape ? CSS.escape(String(instanceId)) : String(instanceId);
    const template = document.querySelector(`[data-instance="${safeId}"] .template-canvas`);
    if (!template) {
      toastr.error('Could not determine template instance');
      return;
    }
    this.isReplacedOnce = false;
    this.isConfirmedOnce = true;
    this.confirmedImageType = this.getSidebarImageType();
    this.batchHandled = false;
    await this.handleBriefReferenceDrop(refs, template, instanceId, null, true);
  }

  getObjectOnWhichDrop({dropEvent, targetPreview, template, type = null}) {
    if (!dropEvent || !targetPreview?.canvas) {
      return null;
    }
    const canvas = targetPreview.canvas;

    const dropCoordinates = this.getRealCanvasCoordinates(dropEvent, targetPreview, template);

    if (type === 'background') {
      const foundObjects = canvas.getObjects().filter((obj) =>
        obj.fieldType === FIELD_TYPE_BACKGROND_THEME_IMAGE &&
        this.isPointInsideObject(dropCoordinates, obj)
      );

      const foundObject = foundObjects[foundObjects.length - 1]; // pick the top image if multiple images found

      return foundObject || null;
    }

    const foundObject = canvas.getObjects().find((obj) =>
      obj.fieldType === FIELD_TYPE_PRODUCT_IMAGE &&
      this.isPointInsideObject(dropCoordinates, obj)
    );

    return foundObject || null;
  }
}

export default TemplateDragDropService;
